'use client';

import { forwardRef, useMemo } from 'react';
import { addMinutes, format as formatDate, parse } from 'date-fns';
import { CheckCircle2Icon, CircleDashedIcon, CircleXIcon, ClockIcon } from 'lucide-react';

import { ApiSchemas } from '@/components/providers/v1-api';
import { cn } from '@/lib/utils';

export type GenericEventRow = {
  type: 'service' | 'non-service';
  start_time?: string | null;
  end_time?: string | null;
  duration_minutes?: number | null;
  window_start_time?: string | null;
  window_end_time?: string | null;
  title?: string | null;
  status?: ApiSchemas['ServiceEventStatus'] | 'PREVIEW' | null;
  highlight?: boolean;
};

export const EventRow = forwardRef<HTMLDivElement, GenericEventRow>(function EventRow(
  {
    type,
    start_time,
    end_time,
    duration_minutes,
    window_start_time,
    window_end_time,
    title,
    status,
    highlight,
  }: GenericEventRow,
  ref
) {
  const { startLabel, endLabel } = useMemo(() => {
    if (!start_time) return { startLabel: '—', endLabel: '—' };
    const start = parse(start_time, 'HH:mm:ss', new Date(0));

    let end: Date;
    if (end_time) {
      end = parse(end_time, 'HH:mm:ss', new Date(0));
    } else if (duration_minutes) {
      end = addMinutes(start, duration_minutes);
    } else {
      end = addMinutes(start, 30);
    }

    return {
      startLabel: formatDate(start, 'h:mm a'),
      endLabel: formatDate(end, 'h:mm a'),
    };
  }, [start_time, end_time, duration_minutes]);

  const timeWindowLabel = useMemo(() => {
    if (!window_start_time || !window_end_time) return '';
    const start = timeToTenth(window_start_time);
    const end = timeToTenth(window_end_time);
    return `${start} - ${end}`;
  }, [window_start_time, window_end_time]);

  return (
    <div
      ref={ref}
      className={cn(
        'hover:bg-muted/50 flex items-center border-b px-2 py-2 text-sm last:border-b-0',
        highlight && 'rounded-md !border-2 border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
      )}
    >
      <div className='w-16 text-center font-semibold'>{startLabel}</div>
      <div className='w-16 text-center'>{endLabel}</div>

      <div className={cn('ml-2 font-medium', type === 'non-service' && 'text-muted-foreground')}>
        {title || (type === 'non-service' ? 'Blocked' : 'No customer name')}
      </div>

      <div className='ml-auto flex items-center gap-2'>
        <div className='bg-muted rounded-md px-1.5 py-px text-center'>
          <div className='text-muted-foreground text-[10px] tracking-tight whitespace-nowrap'>{timeWindowLabel}</div>
        </div>
        {status && status !== 'PREVIEW' && <StatusIcon status={status} />}
      </div>
    </div>
  );
});

export function StatusIcon({ status, dimmed = false }: { status: ApiSchemas['ServiceEventStatus']; dimmed?: boolean }) {
  const className = cn('h-4 w-4', dimmed && 'opacity-60');
  switch (status) {
    case 'CONFIRMED':
    case 'COMPLETED':
      return <CheckCircle2Icon className={`${className} text-emerald-600`} />;
    case 'CANCELLED':
      return <CircleXIcon className={`${className} text-destructive`} />;
    case 'PENDING':
      return <ClockIcon className={`${className} text-amber-500`} />;
    case 'RESCHEDULED':
      return <CircleDashedIcon className={`${className} text-amber-500`} />;
    case 'NO_SHOW':
      return <CircleDashedIcon className={`${className} text-muted-foreground`} />;
    default:
      return <ClockIcon className={className} />;
  }
}

export function timeToTenth(time: string) {
  const [hours, minutes] = time.split(':').map(Number);
  const h12 = hours % 12 || 12;
  const tenth = Math.floor(minutes / 10);
  return tenth ? `${h12}.${tenth}` : String(h12);
}
